'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    deleteLetterhead,
    getLetterheads,
    Letterhead,
    setDefaultLetterhead,
} from '@/lib/api/letterheads/letterhead-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdBrandingWatermark,
    MdDelete,
    MdEdit,
    MdStar,
    MdStarBorder,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const MODE_LABELS: Record<string, string> = {
    BANDS: 'Cabeçalho e rodapé',
    FULL_PAGE: 'Página inteira',
    RESERVE: 'Somente margens',
}

export default function LetterheadsListPage() {
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'registrations',
        'letterheads',
    )
    const [letterheads, setLetterheads] = useState<Letterhead[]>([])
    const [loading, setLoading] = useState(true)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [itemToDelete, setItemToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        loadLetterheads()
    }, [])

    const loadLetterheads = async () => {
        setLoading(true)
        try {
            const response = await getLetterheads()
            if (response.success) {
                setLetterheads(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar timbrados')
            }
        } catch {
            toast.error('Erro ao carregar timbrados')
        } finally {
            setLoading(false)
        }
    }

    const handleSetDefault = async (id: number) => {
        const response = await setDefaultLetterhead(id)
        if (response.success) {
            toast.success('Timbrado definido como padrão')
            loadLetterheads()
        } else {
            toast.error(response.error || 'Erro ao definir o timbrado padrão')
        }
    }

    const handleDelete = (id: number) => {
        setItemToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!itemToDelete) return
        setDeleting(true)
        try {
            const response = await deleteLetterhead(itemToDelete)
            if (response.success) {
                toast.success('Timbrado excluído com sucesso')
                loadLetterheads()
                setShowDeleteModal(false)
                setItemToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir timbrado')
            }
        } catch {
            toast.error('Erro ao excluir timbrado')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando timbrados...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdBrandingWatermark className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Timbrados
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {letterheads.length}{' '}
                            {letterheads.length === 1
                                ? 'timbrado cadastrado'
                                : 'timbrados cadastrados'}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() => navigate('/registrations/letterheads/new')}
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Adicionar</span>
                    </button>
                )}
            </div>

            {letterheads.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdBrandingWatermark className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center max-w-md">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhum timbrado cadastrado
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Sem timbrado, as impressões e os PDFs saem no layout
                            padrão do sistema, com o logo e os dados da filial.
                        </p>
                        {canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/registrations/letterheads/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeiro timbrado
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {letterheads.map(letterhead => (
                        <div
                            key={letterhead.id}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300"
                        >
                            <div className="flex items-center gap-4 p-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <h3 className="text-base font-semibold text-base-content">
                                            {letterhead.name}
                                        </h3>
                                        {letterhead.isDefault && (
                                            <span className="badge badge-secondary badge-sm gap-1">
                                                <MdStar className="text-sm" />
                                                Padrão
                                            </span>
                                        )}
                                        {!letterhead.active && (
                                            <span className="badge badge-ghost badge-sm">
                                                Inativo
                                            </span>
                                        )}
                                        <span className="badge badge-outline badge-sm">
                                            {letterhead.pageSize}
                                        </span>
                                        <span className="badge badge-outline badge-sm">
                                            {MODE_LABELS[letterhead.mode] ||
                                                letterhead.mode}
                                        </span>
                                    </div>
                                    <p className="text-sm text-base-content/60">
                                        {letterhead.documentTypes.length > 0
                                            ? `${letterhead.documentTypes.length} ${
                                                  letterhead.documentTypes
                                                      .length === 1
                                                      ? 'documento vinculado'
                                                      : 'documentos vinculados'
                                              }`
                                            : 'Sem vínculo por documento'}
                                        {letterhead.description
                                            ? ` · ${letterhead.description}`
                                            : ''}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1">
                                    {canUpdate && !letterhead.isDefault && (
                                        <button
                                            onClick={() =>
                                                handleSetDefault(letterhead.id)
                                            }
                                            className="btn btn-sm btn-ghost"
                                            title="Definir como padrão"
                                        >
                                            <MdStarBorder className="text-lg" />
                                        </button>
                                    )}
                                    {canUpdate && (
                                        <button
                                            onClick={() =>
                                                navigate(
                                                    `/registrations/letterheads/${letterhead.id}`,
                                                )
                                            }
                                            className="btn btn-sm btn-ghost"
                                            title="Editar"
                                        >
                                            <MdEdit className="text-lg" />
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button
                                            onClick={() =>
                                                handleDelete(letterhead.id)
                                            }
                                            className="btn btn-sm btn-ghost text-error hover:bg-error/10"
                                            title="Excluir"
                                        >
                                            <MdDelete className="text-lg" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setItemToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir timbrado"
                message="Tem certeza de que deseja excluir este timbrado? Os documentos que o usavam voltam ao layout padrão do sistema."
                confirmButtonText="Excluir"
                loading={deleting}
            />
        </div>
    )
}
